import React, { useMemo, useState, useCallback, useEffect } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import useMicroTrendsListData from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/useMicroTrendsListData';
import {
  useCategoryEntity,
  useCategoryIds,
  useMainEntityId,
  useMainEntityName,
  useMainEntityType,
  useRetailerId,
  useSubcategoryEntity,
  useTrendEntity
} from 'src/utils/Hooks/reduxSelectors';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import { useDebouncedValue, useAppSelector } from 'src/utils/Hooks';
import {
  MicroTrendsCardLoading,
  MicroTrendsChartLoading
} from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/MicroTrendsCardLoading';
import TrendEntitySummary from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/MicroTrendEntityDetail';
import {
  MicroTrendsCard,
  TrendGroupCard
} from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/MicroTrendMainComoponents';
import SegmentNoDataPlaceholder from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentNoDataPlaceholder';
import MicroTrendFilterModal from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/MicroTrendFilterModal';
import {
  useMicroTrendsFilterStore,
  useMicroTrendsTimePeriodStore
} from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/MicroTrendStore';

/**
 * Atlas V2 Trends Page layout - this can be found in only category page
 */

const ITEMS_PER_PAGE = 5; // Number of trend groups per page

const AtlasV2MicroTrendsListPageContent = () => {
  const retailerId = useRetailerId();
  const entityId = useMainEntityId();
  const categoryName = useMainEntityName();
  const mainEntityType = useMainEntityType();
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebouncedValue(searchValue, 700);
  const selectedCategoryIds = useMicroTrendsFilterStore((state) => state.selectedCategoryIds);
  const selectedSubcategoryIds = useMicroTrendsFilterStore((state) => state.selectedSubcategoryIds);
  const setAvailableEndWeek = useMicroTrendsTimePeriodStore((state) => state.setAvailableEndWeek);
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isAllCategoriesEntity } = useCategoryEntity();
  const categoryIds = useCategoryIds();
  // Get all subcategories that belong to the current category
  const subcategories = useAppSelector((state) => state.subCategories);
  const allSubcategoryIds = useMemo(() => {
    if (!subcategories || !entityId) return [];
    return subcategories
      .filter((sub: any) => Number(sub.parentCategoryId) === Number(entityId))
      .map((sub: any) => Number(sub.id));
  }, [subcategories, entityId]);

  const categoryIdsWithoutAll = categoryIds.filter((id) => id !== 0);

  const { effectiveCategoryIds, effectiveSubcategoryIds } = useMemo(() => {
    // If subcategories are selected, use them; otherwise use all subcategories of the category

    if (isSubcategoryEntity) {
      return {
        effectiveCategoryIds: [],
        effectiveSubcategoryIds: [Number(entityId)]
      };
    }

    if (selectedSubcategoryIds.length > 0) {
      return {
        effectiveCategoryIds: [],
        effectiveSubcategoryIds: selectedSubcategoryIds
      };
    }

    // If categories are selected at all categories level, use them
    if (isAllCategoriesEntity && selectedCategoryIds.length > 0) {
      return {
        effectiveCategoryIds: selectedCategoryIds,
        effectiveSubcategoryIds: []
      };
    }

    // If we have subcategories for this category, use them; otherwise fallback to category
    if (allSubcategoryIds.length > 0) {
      return {
        effectiveCategoryIds: [],
        effectiveSubcategoryIds: allSubcategoryIds
      };
    }

    return {
      effectiveCategoryIds: isAllCategoriesEntity ? categoryIdsWithoutAll : [Number(entityId)],
      effectiveSubcategoryIds: []
    };
  }, [
    isSubcategoryEntity,
    selectedCategoryIds,
    selectedSubcategoryIds,
    allSubcategoryIds,
    isAllCategoriesEntity,
    categoryIdsWithoutAll,
    entityId
  ]);

  const {
    filteredTrendGroups: trendGroups,
    isLoading: isSummaryLoading,
    data: allTrendGroups
  } = useMicroTrendsListData({
    categoryIds: effectiveCategoryIds,
    subcategoryIds: effectiveSubcategoryIds,
    retailerId: Number(retailerId),
    query: debouncedSearchValue || undefined
  });

  // Extract the maximum endWeekId from all trends and set it in the store
  useEffect(() => {
    if (allTrendGroups && allTrendGroups.length > 0) {
      const maxEndWeek = allTrendGroups.reduce((max, trendGroup) => {
        const groupMaxEndWeek = trendGroup.trends.reduce((groupMax, trend) => {
          return Math.max(groupMax, trend.endWeekId);
        }, 0);
        return Math.max(max, groupMaxEndWeek);
      }, 0);

      if (maxEndWeek > 0) {
        setAvailableEndWeek(maxEndWeek);
      }
    }
  }, [allTrendGroups, setAvailableEndWeek]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Simple pagination - slice the trend groups directly
  const currentPageData = useMemo(() => {
    if (!trendGroups) return [];
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return trendGroups.slice(startIndex, endIndex);
  }, [trendGroups, currentPage]);

  // Calculate pagination values
  const totalPages = useMemo(() => {
    if (!trendGroups) return 0;
    return Math.ceil(trendGroups.length / ITEMS_PER_PAGE);
  }, [trendGroups]);

  const hasNextPage = currentPage < totalPages;

  const scrollToTop = useCallback(() => {
    const osViewport = document.querySelector('.os-viewport');
    if (osViewport) {
      if (osViewport.scrollTo) {
        osViewport.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
  }, []);

  const handleClickNext = useCallback(() => {
    setCurrentPage((prev) => prev + 1);
    setTimeout(scrollToTop, 100);
  }, [scrollToTop]);

  const handleClickPrevious = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      setTimeout(scrollToTop, 100);
    }
  }, [currentPage, scrollToTop]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  };

  // Reset page when search query changes
  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchValue, selectedSubcategoryIds]);
  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={32}>
        <Flex flexDirection="column" gap={8}>
          <Text variant="h4">Microtrends</Text>
          <Text variant="body1" sx={{ lineHeight: '24px' }}>
            The chart displays the market share of retail sales of trending subjects in
            {isAllCategoriesEntity && categoryIds?.length !== 1
              ? `${' '}all ${categoryIds.length - 1} categories`
              : `${' '}the ${categoryName}`}{' '}
            {isAllCategoriesEntity ? '' : mainEntityType} during the last 52 week time period.
          </Text>
        </Flex>

        <Flex alignItems="center" justifyContent="space-between" gap={16}>
          <SearchTextField placeholder="Search microtrends" value={searchValue} onChange={handleSearchChange} />
        </Flex>

        {isSummaryLoading ? (
          <Flex flexDirection="column" gap={32}>
            {[1, 2, 3, 4, 5].map((item) => (
              <Flex flexDirection="column" gap={32} key={item}>
                <MicroTrendsCard>
                  <Flex gap={32}>
                    <MicroTrendsCardLoading />
                    <MicroTrendsChartLoading />
                  </Flex>
                </MicroTrendsCard>
              </Flex>
            ))}
          </Flex>
        ) : (
          <>
            {currentPageData.map((trendGroup) => (
              <TrendGroupCard key={trendGroup.trendGroupId} trendGroup={trendGroup} />
            ))}

            {trendGroups && trendGroups.length === 0 && !isSummaryLoading && (
              <Flex justifyContent="center" marginTop="32px">
                {searchValue ? (
                  <SegmentNoDataPlaceholder
                    title="No search results found"
                    subtitle={
                      searchValue ? 'There is no data available for the search term.' : 'There is no data available.'
                    }
                    maxWidth={238}
                  />
                ) : (
                  <SegmentNoDataPlaceholder
                    title="No data available"
                    subtitle="There is no data available."
                    maxWidth={238}
                  />
                )}
              </Flex>
            )}

            {(currentPage > 1 || hasNextPage) && (
              <Flex justifyContent="flex-end" alignItems="center" marginTop="-8px">
                <Pagination
                  currentPage={currentPage}
                  onClickNext={handleClickNext}
                  shouldUseInfinitePagination={true}
                  onClickPrevious={handleClickPrevious}
                  disableNextButton={isSummaryLoading || !hasNextPage}
                />
              </Flex>
            )}
          </>
        )}
      </Flex>
    </BasePageContainer>
  );
};

export const AtlasV2MicroTrendsListPageLayout = () => {
  const { isTrendEntity } = useTrendEntity();

  if (isTrendEntity) {
    return <TrendEntitySummary />;
  }

  return (
    <>
      <MicroTrendFilterModal />
      <AtlasV2MicroTrendsListPageContent />
    </>
  );
};
